/**
 * Построение графика цены (T11.9, `05_API.md` §6).
 *
 * ## Ступени, а не наклонная линия
 *
 * Цена держится неизменной, пока её не поменяли. Соединить две точки
 * прямой значит утверждать, что в промежутке она плавно снижалась, — то
 * есть показать цены, которых никогда не было. Особенно скверно это на
 * редких изменениях: объявление, подешевевшее один раз месяц назад,
 * выглядело бы как медленно дешевеющее весь месяц.
 *
 * Поэтому ломаная идёт ступенями: горизонталь до момента изменения,
 * вертикаль в новую цену.
 *
 * ## Правый край — «сейчас», а не последнее изменение
 *
 * Последняя цена действует до сих пор, и обрывать линию на дате
 * изменения значит показывать, будто дальше цены нет. Отрезок
 * дотягивается до текущего момента.
 *
 * ## Ширина в единицах картинки, а не в точках экрана
 *
 * Картинка растягивается по ширине карточки, поэтому расчёт идёт в своей
 * системе координат, а толщину линии держит `non-scaling-stroke` в
 * стилях. Считать в точках экрана значило бы пересчитывать всё на каждом
 * повороте телефона.
 */

/** Точка ряда в том виде, в каком её отдаёт API. */
export interface PricePoint {
  readonly at: string;
  readonly price_minor: string | number;
  readonly kind: 'initial' | 'change' | 'carried';
}

export interface Shape {
  /** Ломаная ступенями для атрибута `d`. */
  readonly path: string;
  /** Та же ломаная, замкнутая книзу: заливка под линией. */
  readonly area: string;
  /** Крайние цены в копейках — для подписей. */
  readonly min: number;
  readonly max: number;
  /** Границы отрезка: первая отметка и «сейчас». */
  readonly from: string;
  readonly to: string;
  /** Насколько цена изменилась за отрезок, в копейках. */
  readonly change: number;
}

/**
 * Доля высоты, оставленная сверху и снизу.
 *
 * Без неё линия при единственном изменении прижималась бы к самым краям
 * картинки и выглядела бы обрезанной.
 */
const PADDING = 0.12;

/** Две цифры после запятой: строка пути умеет вырастать втрое. */
function round(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Строит ломаную.
 *
 * Меньше двух точек — `null`: по одной цене графика не бывает, и точка
 * на пустом поле изображала бы сведения, которых нет.
 */
export function shape(
  points: readonly PricePoint[],
  now: number,
  width: number,
  height: number,
): Shape | null {
  if (points.length < 2) return null;

  const values = points.map((one) => Number(one.price_minor));
  const times = points.map((one) => new Date(one.at).getTime());
  const min = Math.min(...values);
  const max = Math.max(...values);

  const start = times[0] ?? now;
  // Правый край — «сейчас», но не раньше последнего изменения: часы
  // телефона могут отставать от серверных, и отрицательная ширина
  // отрезка дала бы ломаную, идущую назад
  const end = Math.max(now, times.at(-1) ?? now);
  const span = end - start;
  const spread = max - min;

  const x = (time: number): number => (span === 0 ? width : ((time - start) / span) * width);

  // Цена, не менявшаяся в размере, но менявшаяся по времени: линия
  // посередине. Деление на нулевой разброс дало бы NaN и пустой график
  const y = (value: number): number =>
    spread === 0
      ? height / 2
      : height * (1 - PADDING) - ((value - min) / spread) * height * (1 - 2 * PADDING);

  const steps: string[] = [`M ${String(round(x(start)))} ${String(round(y(values[0] ?? 0)))}`];

  for (let at = 1; at < points.length; at += 1) {
    const time = times[at] ?? start;
    const value = values[at] ?? 0;

    // Горизонталь до момента изменения, затем вертикаль в новую цену
    steps.push(`H ${String(round(x(time)))}`, `V ${String(round(y(value)))}`);
  }

  steps.push(`H ${String(round(x(end)))}`);

  const path = steps.join(' ');

  return {
    path,
    // Заливка — та же ломаная, опущенная к низу картинки. Отдельным
    // расчётом она разошлась бы с линией при первой же правке
    area: `${path} V ${String(round(height))} H ${String(round(x(start)))} Z`,
    min,
    max,
    from: points[0]?.at ?? new Date(start).toISOString(),
    to: new Date(end).toISOString(),
    change: (values.at(-1) ?? 0) - (values[0] ?? 0),
  };
}
